import { useCallback } from 'react';

import {
  useAttachSurveyToGroupsMutation,
  useErpSurveyAssignmentsQuery,
} from 'core/graphql';

import { ISurveyAssignmentRow } from './analyticsModel';
import { ISurveyTargetingSkip } from './surveyModel';

interface IUseSurveyAssignmentsArgs {
  surveyId?: string;
  onlyActive?: boolean;
  origin?: string;
  search?: string;
}

/**
 * Spec 0038 — the survey's audience, seen FROM the survey. Until this
 * existed the ERP could only show a COUNT of attachments: the backend knew
 * `groupSurveyAssignments(groupId)` and nothing in the other direction.
 */
export const useSurveyAssignments = ({
  surveyId,
  onlyActive,
  origin,
  search,
}: IUseSurveyAssignmentsArgs) => {
  const { data, loading, refetch } = useErpSurveyAssignmentsQuery({
    variables: { surveyId: surveyId as string, onlyActive, origin, search },
    skip: !surveyId,
    fetchPolicy: 'cache-and-network',
  });

  return {
    rows: (data?.surveyAssignments || []) as ISurveyAssignmentRow[],
    loading,
    refetch,
  };
};

interface IAttachResult {
  success: boolean;
  createdCount: number;
  skipped: ISurveyTargetingSkip[];
  errorCode?: string;
}

/**
 * Bulk MANUAL attach. `success: true` WITH a non-empty `skipped[]` is the
 * normal outcome — a group missing lesson N must not fail the other twenty —
 * so the caller reports both, exactly like the targeting save does.
 */
export const useAttachSurveyToGroups = () => {
  const [mutate, { loading }] = useAttachSurveyToGroupsMutation({
    // Operation NAMES, not backend field names. A rename on either side
    // makes these silently no-ops, which is why the survey card refetches
    // through the hook's own `refetch` as well.
    refetchQueries: ['ErpSurveyAssignments', 'ErpSurveysList'],
  });

  const attach = useCallback(
    async (
      surveyId: string,
      groupIds: string[],
      triggerKind: string,
      triggerLessonNumber?: number,
    ): Promise<IAttachResult> => {
      const { data } = await mutate({
        variables: { surveyId, groupIds, triggerKind, triggerLessonNumber },
      });
      const payload = data?.assignSurveyToGroups;
      return {
        success: Boolean(payload?.success),
        createdCount: payload?.createdCount || 0,
        skipped: (payload?.skipped || []) as ISurveyTargetingSkip[],
        errorCode: payload?.customErrors?.[0]?.code || undefined,
      };
    },
    [mutate],
  );

  return { attach, loading };
};
